<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import PipelinesAPI from 'dashboard/api/pipelines';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  pipeline: { type: Object, required: true }, // pipeline a editar
});
const emit = defineEmits(['close', 'updated']);

const store = useStore();

// Cargar datos del pipeline existente
const name = ref(props.pipeline.name || '');
const selectedBranchIds = ref([...(props.pipeline.scope_branch_ids || [])]);
const saving = ref(false);
const errorMsg = ref(null);

const availableBranches = computed(() => store.getters['branches/getBranches'] || []);
const allPipelines = ref([]);

function toggleBranch(id) {
  const idx = selectedBranchIds.value.indexOf(id);
  if (idx === -1) selectedBranchIds.value.push(id);
  else selectedBranchIds.value.splice(idx, 1);
}
function selectAllBranches() {
  selectedBranchIds.value = availableBranches.value.map(b => b.id);
}
function clearAllBranches() {
  selectedBranchIds.value = [];
}

const branchSummary = computed(() => {
  const total = availableBranches.value.length;
  const selected = selectedBranchIds.value.length;
  if (selected === 0) return { text: `Sin selección · aplica a TODAS las ${total} sucursales (modo global)`, count: total };
  if (selected === total) return { text: `Las ${total} sucursales seleccionadas`, count: total };
  return { text: `${selected} de ${total} sucursales seleccionadas`, count: selected };
});

// Detección de duplicados (mismo nombre, distinto ID)
const duplicateWarning = computed(() => {
  if (!name.value || !name.value.trim()) return null;
  const cleanName = name.value.trim().toLowerCase();
  const dupes = allPipelines.value.filter(p =>
    p.id !== props.pipeline.id && (p.name || '').trim().toLowerCase() === cleanName
  );
  if (dupes.length === 0) return null;
  return { count: dupes.length, ids: dupes.map(p => p.id) };
});

onMounted(async () => {
  store.dispatch('branches/get');
  try {
    const { data } = await PipelinesAPI.get();
    allPipelines.value = data?.payload || [];
  } catch (e) { /* silent */ }
});

async function save() {
  if (!name.value || !name.value.trim()) return;
  saving.value = true;
  errorMsg.value = null;
  try {
    const { data } = await PipelinesAPI.update(props.pipeline.id, {
      pipeline: {
        name: name.value.trim(),
        scope_branch_ids: selectedBranchIds.value,
      },
    });
    emit('updated', data);
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="epd-overlay" @click.self="emit('close')">
    <div class="epd-modal" role="dialog" aria-modal="true">
      <!-- HEAD -->
      <div class="epd-head">
        <div class="epd-title-wrap">
          <div class="epd-title-icon">✏</div>
          <div>
            <div class="epd-title">Editar embudo</div>
            <div class="epd-subtitle">Modifica nombre y sucursales · {{ pipeline.pipeline_stages?.length || pipeline.stages?.length || 0 }} etapas</div>
          </div>
        </div>
        <button class="epd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <!-- BODY scrolleable -->
      <div class="epd-body">
        <div class="epd-field-label">Nombre del embudo</div>
        <input v-model="name" placeholder="ej: DTO. DECORACIÓN" class="epd-input" />

        <!-- Detección de duplicados -->
        <div v-if="duplicateWarning" class="epd-dupe-warning">
          ⚠ Ya existe {{ duplicateWarning.count }} embudo{{ duplicateWarning.count > 1 ? 's' : '' }} con este nombre exacto (id{{ duplicateWarning.count > 1 ? 's' : '' }}: {{ duplicateWarning.ids.join(', ') }}). Considera consolidar o usar un nombre distinto.
        </div>

        <!-- SUCURSALES -->
        <div v-if="availableBranches.length > 0" class="epd-section">
          <div class="epd-section-head">
            <div class="epd-section-title">🏢 Sucursales donde aplicar</div>
            <div class="epd-bulk-tools">
              <button type="button" class="epd-bulk-btn epd-bulk-all" @click="selectAllBranches">✓ Todas</button>
              <button type="button" class="epd-bulk-btn epd-bulk-none" @click="clearAllBranches">✗ Ninguna</button>
            </div>
          </div>
          <div class="epd-branches-grid">
            <div
              v-for="b in availableBranches"
              :key="b.id"
              class="epd-br-row"
              :class="{ 'is-on': selectedBranchIds.includes(b.id) }"
              @click="toggleBranch(b.id)"
            >
              <div class="epd-br-cb">
                <span v-if="selectedBranchIds.includes(b.id)">✓</span>
              </div>
              <div class="epd-br-icon">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</div>
              <div class="epd-br-info">
                <div class="epd-br-name">{{ b.name }}</div>
                <div class="epd-br-meta">{{ b.is_personal ? 'Tu sucursal · Personal' : 'Sucursal' }}</div>
              </div>
              <div class="epd-br-tag" :class="b.is_personal ? 'is-personal' : 'is-suc'">
                {{ b.is_personal ? 'Personal' : 'Suc' }}
              </div>
            </div>
          </div>

          <div class="epd-summary">
            ⚡ {{ branchSummary.text }} · al editar etapas se sincroniza automáticamente.
          </div>
        </div>

        <div v-if="errorMsg" class="epd-error">⚠ {{ errorMsg }}</div>
      </div>

      <div class="epd-foot">
        <button @click="emit('close')" class="epd-btn epd-cancel" type="button">Cancelar</button>
        <button @click="save" :disabled="saving || !name.trim()" class="epd-btn epd-save" type="button">
          {{ saving ? '⏳ Guardando…' : '⚡ Guardar cambios' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* EditPipelineDialog · M1.B + P1 layout · espejo de NewPipelineDialog */
.epd-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.78); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; font-family: -apple-system, system-ui, sans-serif; }
.epd-modal { width: 540px; max-width: 100%; max-height: calc(100vh - 48px); background: rgba(10,10,10,0.98); border: 1.5px solid rgba(251,191,36,0.45); border-radius: 18px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(251,191,36,0.18); display: flex; flex-direction: column; position: relative; color: #fff; }
.epd-modal::before { content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px; background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(251,191,36,0.10), transparent 60%); z-index: 1; }
.epd-modal > * { position: relative; z-index: 2; }

.epd-head { padding: 18px 22px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between; align-items: center; background: radial-gradient(ellipse at 0% 0%, rgba(251,191,36,0.10), transparent 60%); flex-shrink: 0; }
.epd-title-wrap { display: flex; align-items: center; gap: 12px; }
.epd-title-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.10)); border: 1.5px solid rgba(251,191,36,0.40); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.epd-title { font-size: 18px; font-weight: 900; color: #fff; line-height: 1.15; }
.epd-subtitle { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.epd-close { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; line-height: 1; padding: 0; font-family: inherit; transition: all 0.15s; }
.epd-close:hover { background: rgba(239,68,68,0.20); color: #fff; border-color: rgba(239,68,68,0.40); transform: rotate(90deg); }

.epd-body { padding: 18px 22px; flex: 1; overflow-y: auto; }
.epd-field-label { font-size: 11px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight: 800; margin-bottom: 8px; }
.epd-input { width: 100%; padding: 10px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(251,191,36,0.30); border-radius: 8px; color: #fff; font-size: 14px; font-family: inherit; outline: none; }
.epd-input:focus { border-color: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }

.epd-dupe-warning { margin-top: 10px; padding: 10px 12px; background: rgba(245,158,11,0.10); border: 1px solid rgba(245,158,11,0.40); border-radius: 8px; font-size: 11px; color: #fbbf24; line-height: 1.5; }

.epd-section { margin-top: 18px; }
.epd-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.epd-section-title { font-size: 11px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(251,191,36,0.85); font-weight: 800; }
.epd-bulk-tools { display: flex; gap: 4px; }
.epd-bulk-btn { padding: 4px 9px; border-radius: 6px; font-size: 9px; font-weight: 800; cursor: pointer; border: 1px solid; font-family: inherit; }
.epd-bulk-all { background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.45); color: #4ade80; }
.epd-bulk-all:hover { background: rgba(34,197,94,0.20); }
.epd-bulk-none { background: rgba(239,68,68,0.10); border-color: rgba(239,68,68,0.45); color: #f87171; }
.epd-bulk-none:hover { background: rgba(239,68,68,0.20); }

.epd-branches-grid { display: flex; flex-direction: column; gap: 6px; }
.epd-br-row { display: flex; align-items: center; gap: 12px; padding: 11px 14px; background: rgba(255,255,255,0.03); border: 1.5px solid rgba(255,255,255,0.08); border-radius: 10px; cursor: pointer; transition: all 0.18s; }
.epd-br-row:hover { border-color: rgba(251,191,36,0.30); }
.epd-br-row.is-on { background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(217,119,6,0.04)); border-color: rgba(251,191,36,0.55); box-shadow: 0 0 16px rgba(251,191,36,0.18); }
.epd-br-cb { width: 22px; height: 22px; border-radius: 6px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.18); flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; color: transparent; font-weight: 900; }
.epd-br-row.is-on .epd-br-cb { background: linear-gradient(135deg, #fbbf24, #d97706); border-color: #fbbf24; color: #000; box-shadow: 0 0 10px rgba(251,191,36,0.50); }
.epd-br-icon { width: 32px; height: 32px; border-radius: 8px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 900; color: #fbbf24; flex-shrink: 0; letter-spacing: 0.3px; }
.epd-br-row.is-on .epd-br-icon { background: rgba(251,191,36,0.15); border-color: rgba(251,191,36,0.40); }
.epd-br-info { flex: 1; min-width: 0; }
.epd-br-name { font-size: 13px; font-weight: 800; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.epd-br-row.is-on .epd-br-name { color: #fbbf24; }
.epd-br-meta { font-size: 10px; color: rgba(255,255,255,0.45); margin-top: 1px; }
.epd-br-tag { padding: 3px 8px; border-radius: 5px; font-size: 9px; font-weight: 800; letter-spacing: 0.4px; text-transform: uppercase; flex-shrink: 0; }
.epd-br-tag.is-personal { background: rgba(99,102,241,0.18); color: #818cf8; border: 1px solid rgba(99,102,241,0.40); }
.epd-br-tag.is-suc { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.55); border: 1px solid rgba(255,255,255,0.10); }

.epd-summary { margin-top: 14px; padding: 11px 14px; background: rgba(34,197,94,0.06); border: 1px dashed rgba(34,197,94,0.40); border-radius: 9px; font-size: 11px; color: #4ade80; line-height: 1.5; }
.epd-error { margin-top: 14px; padding: 10px 12px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.40); border-radius: 8px; font-size: 11px; color: #f87171; font-weight: 700; }

.epd-foot { padding: 14px 22px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: flex-end; gap: 8px; background: rgba(0,0,0,0.4); flex-shrink: 0; }
.epd-btn { padding: 10px 18px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.epd-btn:disabled { opacity: 0.5; cursor: wait; }
.epd-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.08); }
.epd-cancel:hover { background: rgba(255,255,255,0.08); }
.epd-save { background: linear-gradient(135deg, #fbbf24, #d97706); color: #000; box-shadow: 0 0 16px rgba(251,191,36,0.30); }
.epd-save:hover:not(:disabled) { box-shadow: 0 0 24px rgba(251,191,36,0.55); }
</style>
